import { useRef, useState } from 'react';
import { api } from '../api';
import type { Task as TaskData, TaskState, User } from '../api';
import { Link } from '../router';
import { useSession } from '../session';
import { useData } from '../useData';
import { Shell } from '../components/Shell';
import {
  Avatar, Body, Empty, Loading, boardOrder, stateColour,
  usePriorityName, useStateName, when,
} from '../components/bits';
import { RichText } from '../components/RichText';
import type { RichTextHandle } from '../components/RichText';

/** One ticket: what it is, who has it, what was said, and what happened.
 *
 *  A client whose task is waiting for them gets a button that says so, rather
 *  than a dropdown of studio states to pick the right one out of. */
export function Task({ id }: { id: number }) {
  const { t, locale, user } = useSession();
  const stateName = useStateName();
  const priorityName = usePriorityName();

  const task = useData(() => api.task(id), [id]);
  const comments = useData(() => api.comments(id), [id]);
  const events = useData(() => api.taskEvents(id), [id]);
  const files = useData(() => api.taskFiles(id), [id]);
  const [editing, setEditing] = useState(false);
  const people = useData(
    () => (task.data ? api.projectPeople(task.data.projectId) : Promise.resolve([] as User[])),
    [task.data?.projectId],
  );

  if (task.error) return <Shell><Empty>{t.notFound}</Empty></Shell>;
  if (!task.data) return <Shell><Loading /></Shell>;

  const it = task.data;
  const reloadAll = () => { task.reload(); events.reload(); };

  const change = async (patch: Parameters<typeof api.updateTask>[1]) => {
    await api.updateTask(id, patch);
    reloadAll();
  };

  return (
    <Shell>
      <div className="page-head">
        <div className="grow">
          <p className="kicker">
            <Link to={`/projects/${it.projectId}`}>{it.projectTitle}</Link>
          </p>
          <h1>{it.title}</h1>
        </div>
        {user?.isAdmin && (
          <div>
            <button onClick={() => setEditing((v) => !v)}>
              {editing ? t.cancel : t.edit}
            </button>
          </div>
        )}
      </div>

      {editing && (
        <EditTask
          task={it}
          onDone={() => { setEditing(false); task.reload(); }}
          onCancel={() => setEditing(false)}
        />
      )}

      <div className="split">
        <div>
          {it.state === 'in_stage' && !user?.isStaff && (
            <div className="card stack" style={{ marginBottom: 'var(--sp-4)' }}>
              <p style={{ margin: 0 }}>{t.approveHint}</p>
              <div>
                <button
                  className="primary"
                  onClick={async () => { await api.approveTask(id); reloadAll(); }}
                >
                  {t.approve} &gt;
                </button>
              </div>
            </div>
          )}

          <Body html={it.descriptionHtml} />

          <h2
            style={{
              // A task with no description should not leave a hole where one
              // would have been.
              marginTop: it.descriptionHtml ? 'var(--sp-6)' : 0,
              marginBottom: 'var(--sp-3)',
            }}
          >
            {t.comments}
          </h2>
          {comments.loading && !comments.data ? <Loading /> : null}
          {comments.data?.length === 0 ? <Empty>{t.noComments}</Empty> : null}
          {comments.data?.map((c) => (
            <article className={c.internal ? 'comment internal' : 'comment'} key={c.id}>
              <Avatar name={c.authorName} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div className="between">
                  <p className="who">
                    {c.authorName ?? '—'} · {when(c.createdAt, locale)}
                    {c.internal && <span className="badge-internal" title={t.internalComment}>{t.internalBadge}</span>}
                  </p>
                  {user?.isAdmin && (
                    <button
                      title={t.remove}
                      aria-label={t.remove}
                      onClick={async () => {
                        if (!window.confirm(t.confirmDeleteComment)) return;
                        await api.deleteComment(id, c.id);
                        comments.reload();
                        task.reload();
                      }}
                    >
                      &times;
                    </button>
                  )}
                </div>
                <Body html={c.bodyHtml} />
              </div>
            </article>
          ))}

          <WriteComment taskId={id} onPosted={() => { comments.reload(); task.reload(); }} />
        </div>

        <aside className="side">
          <section>
            <dt>{t.state}</dt>
            <dd>
              {user?.isStaff ? (
                <select
                  value={it.state}
                  onChange={(e) => change({ state: e.target.value as TaskState })}
                  style={{ color: stateColour[it.state] }}
                >
                  {boardOrder.map((s) => (
                    <option key={s} value={s}>{stateName(s)}</option>
                  ))}
                </select>
              ) : (
                <span style={{ color: stateColour[it.state] }}>{stateName(it.state)}</span>
              )}
            </dd>

            <dt>{t.priority}</dt>
            <dd>
              {user?.isStaff ? (
                <select
                  value={it.priority}
                  onChange={(e) => change({ priority: e.target.value as 'low' | 'medium' | 'high' })}
                >
                  {(['low', 'medium', 'high'] as const).map((p) => (
                    <option key={p} value={p}>{priorityName(p)}</option>
                  ))}
                </select>
              ) : (
                <span>{priorityName(it.priority)}</span>
              )}
            </dd>

            <dt>{t.assignee}</dt>
            <dd>
              {user?.isStaff && people.data && people.data.length > 0 ? (
                <select
                  value={it.assigneeId ?? ''}
                  onChange={(e) => e.target.value && change({ assigneeId: Number(e.target.value) })}
                >
                  <option value="">—</option>
                  {people.data.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              ) : (
                <span>{it.assigneeName ?? '—'}</span>
              )}
            </dd>

            <dt>{t.date}</dt>
            <dd className="muted">{when(it.createdAt, locale)}{it.createdByName ? ` · ${it.createdByName}` : ''}</dd>
          </section>

          {files.data && files.data.length > 0 && (
            <section>
              <dt>{t.files}</dt>
              <ul className="files">
                {files.data.map((f) => (
                  <li key={f.id}><a href={`/api/attachments/${f.id}`}>{f.filename}</a></li>
                ))}
              </ul>
            </section>
          )}

          {events.data && events.data.length > 0 && (
            <section>
              <dt>{t.history}</dt>
              <ul className="timeline">
                {events.data.map((e) => (
                  <li key={e.id}>
                    {stateName(e.toState)}
                    <span className="muted"> · {e.actorName ?? '—'} · {when(e.createdAt, locale)}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </aside>
      </div>
    </Shell>
  );
}

/** Rewording a ticket is an administrator's job: what a client asked for is
 *  the record of what was asked, so it changes only on purpose.
 *
 *  The description is edited in place rather than in a textarea, because what
 *  is there is HTML — quoted mail from the intake, lists, links — and a
 *  textarea would flatten it the first time anybody fixed a typo. The server
 *  sanitises what comes back, as it does for everything else. */
function EditTask({
  task, onDone, onCancel,
}: {
  task: TaskData;
  onDone: () => void;
  onCancel: () => void;
}) {
  const { t } = useSession();
  const [title, setTitle] = useState(task.title);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const description = useRef<RichTextHandle>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setProblem(null);
    try {
      await api.updateTask(task.id, {
        title: title.trim(),
        descriptionHtml: description.current?.html() ?? '',
      });
      onDone();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="card stack" style={{ marginBottom: 'var(--sp-5)' }}>
      <div>
        <label htmlFor="task-title">{t.title}</label>
        <input id="task-title" required autoFocus value={title} onChange={(e) => setTitle(e.target.value)} />
      </div>
      <div>
        <label id="task-description-label">{t.description}</label>
        <RichText
          ref={description}
          labelledBy="task-description-label"
          initialHtml={task.descriptionHtml}
          owner={{ type: 'task', id: task.id }}
          onPendingChange={setUploading}
        />
      </div>
      {problem && <p className="error" role="alert">{problem}</p>}
      <div className="row">
        <button className="primary" type="submit" disabled={busy || uploading || !title.trim()}>{t.save} &gt;</button>
        <button type="button" onClick={onCancel}>{t.cancel}</button>
      </div>
    </form>
  );
}

function WriteComment({ taskId, onPosted }: { taskId: number; onPosted: () => void }) {
  const { t, user } = useSession();
  const editor = useRef<RichTextHandle>(null);
  const [empty, setEmpty] = useState(true);
  const [internal, setInternal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const post = async (event: React.FormEvent) => {
    event.preventDefault();
    const html = editor.current?.html() ?? '';
    if (!html) return;
    setBusy(true);
    setProblem(null);
    try {
      await api.addComment(taskId, html, internal);
      editor.current?.clear();
      onPosted();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={post} className="stack" style={{ marginTop: 'var(--sp-4)' }}>
      <div>
        <label id="comment-label">{t.writeComment}</label>
        <RichText
          ref={editor} labelledBy="comment-label" compact
          owner={{ type: 'task', id: taskId }}
          onPendingChange={setUploading} onEmptyChange={setEmpty}
        />
      </div>
      {user?.isStaff && (
        <label style={{ textTransform: 'none', letterSpacing: 0 }}>
          <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)}
            style={{ width: 'auto', marginRight: 8 }} />
          {t.internalComment}
        </label>
      )}
      {problem && <p className="error" role="alert">{problem}</p>}
      <div>
        <button className="primary" type="submit" disabled={busy || uploading || empty}>{t.post} &gt;</button>
      </div>
    </form>
  );
}
